import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Logo } from '@/components/ui/Logo'
import { FormField } from '@/components/ui/FormField'

type SignupRole = 'school' | 'instructor' | 'student'

const ROLE_INFO: Record<SignupRole, { title: string; body: string }> = {
  school:     { title: 'Driving school',        body: 'Multiple instructors, seats & reporting' },
  instructor: { title: 'Individual instructor', body: 'You teach under your own licence' },
  student:    { title: 'Student',               body: 'You were given an invite code' },
}

const PERKS: Record<SignupRole, string[]> = {
  school:     ['Instructor scorecards and pass-rate reporting', 'Seat pooling and billing across your roster', 'Certificate issuing gated on mock-test results', 'Audit-ready outcome logs'],
  instructor: ['Enroll students in under two minutes', 'Full MTO-aligned mock G2 test', 'Automatic readiness scoring per skill', 'Issue completion certificates without a school'],
  student:    ['20-hour road safety course on any device', 'Track every in-car skill', 'Know your readiness score before you book', 'Your G1 expiry tracked for you'],
}

const PANEL_TITLE: Record<SignupRole, string> = {
  school:     'Your whole team on one standard.',
  instructor: 'Everything a school gives you, minus the school.',
  student:    'See exactly what stands between you and your G2.',
}

function mapAuthError(msg: string): string {
  if (msg.includes('already registered') || msg.includes('User already registered'))
    return 'An account with this email already exists. Try signing in instead.'
  if (msg.includes('Password should be at least'))
    return 'Password must be at least 8 characters.'
  if (msg.includes('Invalid email'))
    return 'Please enter a valid email address.'
  return 'Registration failed. Please try again.'
}

export default function Register() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialRole = (searchParams.get('role') as SignupRole | null) ?? 'school'

  const [role, setRole] = useState<SignupRole>(initialRole)

  // Common fields
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [terms,    setTerms]    = useState(false)

  // School-specific
  const [schoolName,  setSchoolName]  = useState('')
  const [ownerName,   setOwnerName]   = useState('')
  const [mtoLicence,  setMtoLicence]  = useState('')

  // Instructor-specific
  const [fullName,    setFullName]    = useState('')
  const [phone,       setPhone]       = useState('')
  const [licenceNum,  setLicenceNum]  = useState('')
  const [serviceArea, setServiceArea] = useState('')

  // Student-specific
  const [dob,         setDob]         = useState('')
  const [g1Licence,   setG1Licence]   = useState('')
  const [g1Issue,     setG1Issue]     = useState('')
  const [inviteToken, setInviteToken] = useState('')

  const [loading,       setLoading]       = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error,         setError]         = useState('')

  const handleGoogleSignUp = async () => {
    if (googleLoading) return
    setError('')
    setGoogleLoading(true)
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback?role=${role}`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      })
      if (oauthError) setError('Google sign-up failed. Please try again.')
    } finally {
      setGoogleLoading(false)
    }
  }

  const validate = (): string | null => {
    if (!email.trim())    return 'Email is required.'
    if (!password.trim()) return 'Password is required.'
    if (password.length < 8) return 'Password must be at least 8 characters.'
    if (!terms) return 'You must accept the terms to continue.'

    if (role === 'school') {
      if (!schoolName.trim()) return 'School name is required.'
      if (!ownerName.trim())  return 'Owner / admin name is required.'
    }
    if (role === 'instructor') {
      if (!fullName.trim()) return 'Full name is required.'
    }
    if (role === 'student') {
      if (!fullName.trim())    return 'Full name is required.'
      if (!inviteToken.trim()) return 'Invite code is required. Ask your instructor for one.'
    }
    return null
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading) return

    const validationError = validate()
    if (validationError) { setError(validationError); return }
    setError('')

    setLoading(true)
    try {
      // 1. Create Supabase auth user with role metadata
      // The trigger will create the profiles row from this metadata.
      const userMeta: Record<string, string> = {
        primary_role: role === 'school' ? 'school_admin' : role,
        full_name:    role === 'school' ? ownerName : fullName,
      }
      if (role === 'school')      { userMeta.school_name = schoolName; userMeta.mto_licence = mtoLicence; }
      if (role === 'instructor')  { userMeta.phone = phone; userMeta.licence_number = licenceNum; userMeta.service_area = serviceArea; }
      if (role === 'student')     { userMeta.date_of_birth = dob; userMeta.g1_licence = g1Licence; userMeta.g1_issue_date = g1Issue; }

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: userMeta },
      })
      if (signUpError) { setError(mapAuthError(signUpError.message)); return }
      if (!authData.user) { setError('Registration failed. Please try again.'); return }

      const userId = authData.user.id

      // Ensure profile row exists (trigger may lag behind — upsert is safe either way)
      await supabase.from('profiles').upsert({
        id:           userId,
        email:        email.trim(),
        full_name:    role === 'school' ? ownerName.trim() : fullName.trim(),
        primary_role: role === 'school' ? 'school_admin' : role,
        account_status: 'active',
      }, { onConflict: 'id' })

      // 2. Create role-specific records (session is active because email confirmation is disabled)
      if (role === 'school') {
        const { data: school, error: schoolError } = await supabase
          .from('schools')
          .insert({ name: schoolName, mto_licence_number: mtoLicence || null })
          .select('id')
          .single()
        if (schoolError) {
          console.error('School creation failed:', schoolError)
          // Proceed anyway — user can set up school on next login
        } else if (school) {
          await supabase.from('school_memberships').insert({
            user_id: userId, school_id: school.id, role: 'OWNER', status: 'active',
          })
        }
      }

      if (role === 'instructor') {
        await supabase.from('instructor_profiles').insert({
          user_id: userId,
          licence_number: licenceNum || null,
          service_area:   serviceArea || null,
        })
        // Phone is stored in the profile (updated separately)
        await supabase.from('profiles').update({ phone: phone || null }).eq('id', userId)
      }

      if (role === 'student') {
        // Validate invite token server-side before creating student
        if (inviteToken.trim()) {
          const { data: inv } = await supabase
            .from('invitations')
            .select('id, instructor_id, school_id, status, expires_at, intended_role')
            .eq('token_hash', await hashToken(inviteToken.trim()))
            .eq('status', 'pending')
            .single()

          if (!inv) {
            setError('Invite code is invalid or has already been used.')
            // Sign the user out since we can't properly set them up
            await supabase.auth.signOut()
            return
          }
          if (new Date(inv.expires_at) < new Date()) {
            setError('This invite code has expired. Ask your instructor for a new one.')
            await supabase.auth.signOut()
            return
          }

          await supabase.from('student_profiles').insert({
            user_id:          userId,
            date_of_birth:    dob || null,
            g1_licence_number: g1Licence || null,
            g1_issue_date:    g1Issue || null,
          })

          // Establish the instructor relationship
          await supabase.from('instructor_student_relationships').insert({
            instructor_id: inv.instructor_id!,
            student_id:    userId,
            school_id:     inv.school_id,
            status:        'active',
            started_at:    new Date().toISOString(),
          })

          // Mark invitation as accepted
          await supabase.from('invitations').update({
            status: 'accepted', accepted_at: new Date().toISOString(),
          }).eq('id', inv.id)
        } else {
          await supabase.from('student_profiles').insert({
            user_id:          userId,
            date_of_birth:    dob || null,
            g1_licence_number: g1Licence || null,
            g1_issue_date:    g1Issue || null,
          })
        }
      }

      // 3. Navigate to portal
      const portalMap = { school: '/app/school', instructor: '/app/instructor', student: '/app/student' }
      navigate(portalMap[role], { replace: true })

    } finally {
      setLoading(false)
    }
  }

  const panelPerks = PERKS[role]
  const submitLabel = {
    school:     'Register your driving school',
    instructor: 'Register as an instructor',
    student:    'Create student account',
  }[role]

  return (
    <div className='reg-layout'>
      {/* ---- Left: form ---- */}
      <div style={{ padding: '36px 32px', overflowY: 'auto', background: '#FAF6EF', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, cursor: 'pointer' }} onClick={() => navigate('/')}>
          <Logo size={28} />
          <div style={{ font: '500 13px Inter,sans-serif', color: 'rgba(27,42,74,0.4)', marginLeft: 4 }}>← back</div>
        </div>

        <div style={{ font: '600 28px Lora,serif', color: '#1B2A4A' }}>Create your account</div>
        <div style={{ font: '400 14px Inter,sans-serif', color: 'rgba(27,42,74,0.6)', marginTop: 4, marginBottom: 20 }}>
          Pick your account type
        </div>

        {/* Role cards */}
        <div className='g3' style={{ marginBottom: 20 }}>
          {(['school', 'instructor', 'student'] as SignupRole[]).map(r => (
            <div
              key={r}
              onClick={() => setRole(r)}
              style={{
                cursor: 'pointer', background: '#fff',
                border: `2px solid ${role === r ? '#0F7C6C' : 'rgba(27,42,74,0.1)'}`,
                borderRadius: 12, padding: 16,
              }}
            >
              <div style={{ font: '600 14px Inter,sans-serif', color: '#1B2A4A' }}>{ROLE_INFO[r].title}</div>
              <div style={{ font: '400 12px/1.4 Inter,sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 4 }}>{ROLE_INFO[r].body}</div>
            </div>
          ))}
        </div>

        {/* Google sign-up */}
        <button
          type='button'
          onClick={handleGoogleSignUp}
          disabled={googleLoading || loading}
          style={{
            width: '100%', padding: '11px 16px', marginBottom: 14,
            background: '#fff', border: '1px solid rgba(27,42,74,0.15)',
            borderRadius: 10, font: '500 14px Inter,sans-serif', color: '#1B2A4A',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, opacity: googleLoading ? 0.6 : 1,
          }}
        >
          <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z' fill='#4285F4'/>
            <path d='M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z' fill='#34A853'/>
            <path d='M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z' fill='#FBBC05'/>
            <path d='M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z' fill='#EA4335'/>
          </svg>
          {googleLoading ? 'Redirecting…' : `Sign up with Google as ${ROLE_INFO[role].title}`}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: 'rgba(27,42,74,0.1)' }} />
          <div style={{ font: '400 11.5px Inter,sans-serif', color: 'rgba(27,42,74,0.4)' }}>or</div>
          <div style={{ flex: 1, height: 1, background: 'rgba(27,42,74,0.1)' }} />
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 15, padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          {/* School fields */}
          {role === 'school' && (
            <>
              <div className='fg2'>
                <FormField label='School name'       value={schoolName} onChange={e => setSchoolName(e.target.value)}  placeholder='Maple Grove Driving School' required />
                <FormField label='Owner / admin name' value={ownerName}  onChange={e => setOwnerName(e.target.value)}   placeholder='Marcus Grewal' required />
              </div>
              <div className='fg2'>
                <FormField label='MTO school licence #' value={mtoLicence}  onChange={e => setMtoLicence(e.target.value)}  placeholder='DS-000000' />
                <FormField label='Instructor seats'     value=''            placeholder='e.g. 8' type='number' min='1' />
              </div>
            </>
          )}

          {/* Instructor fields */}
          {role === 'instructor' && (
            <>
              <div className='fg2'>
                <FormField label='Full name' value={fullName}    onChange={e => setFullName(e.target.value)}    placeholder='Dana Whitfield' required />
                <FormField label='Phone'     value={phone}       onChange={e => setPhone(e.target.value)}       placeholder='(905) 555-0142' type='tel' />
              </div>
              <div className='fg2'>
                <FormField label='Instructor licence #' value={licenceNum}  onChange={e => setLicenceNum(e.target.value)}  placeholder='INS-000000' />
                <FormField label='Service area'         value={serviceArea} onChange={e => setServiceArea(e.target.value)} placeholder='Mississauga, ON' />
              </div>
            </>
          )}

          {/* Student fields */}
          {role === 'student' && (
            <>
              <div className='fg2'>
                <FormField label='Full name'    value={fullName} onChange={e => setFullName(e.target.value)} placeholder='Priya Raman' required />
                <FormField label='Date of birth' value={dob}     onChange={e => setDob(e.target.value)}      type='date' />
              </div>
              <div className='fg3'>
                <FormField label='G1 licence #'  value={g1Licence} onChange={e => setG1Licence(e.target.value)} placeholder='R1234-56789' />
                <FormField label='G1 issue date' value={g1Issue}   onChange={e => setG1Issue(e.target.value)}   type='date' />
                <FormField label='Invite code'   value={inviteToken} onChange={e => setInviteToken(e.target.value)} placeholder='From your instructor' required />
              </div>
            </>
          )}

          {/* Common fields */}
          <div className='fg2'>
            <FormField label='Email'    value={email}    onChange={e => setEmail(e.target.value)}    placeholder='you@example.com' type='email' autoComplete='email' required />
            <FormField label='Password' value={password} onChange={e => setPassword(e.target.value)} placeholder='Min 8 characters' type='password' autoComplete='new-password' required />
          </div>

          {/* Terms */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div
              onClick={() => setTerms(!terms)}
              style={{
                width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
                border: `1.5px solid ${terms ? '#0F7C6C' : 'rgba(27,42,74,0.25)'}`,
                background: terms ? '#0F7C6C' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', font: '700 11px Inter,sans-serif', cursor: 'pointer',
              }}
            >
              {terms ? '✓' : ''}
            </div>
            <div style={{ font: '400 12.5px/1.5 Inter,sans-serif', color: 'rgba(27,42,74,0.6)', userSelect: 'none', cursor: 'pointer' }} onClick={() => setTerms(!terms)}>
              I agree to the DriveTrack terms and Ontario driver-training record-keeping requirements.
            </div>
          </div>

          {error && <div className='banner-error'>{error}</div>}

          <button type='submit' className='btn-primary' disabled={loading}>
            {loading ? 'Creating account…' : submitLabel}
          </button>

          <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center' }}>
            Already registered?{' '}
            <Link to='/login' style={{ fontWeight: 600, color: '#0F7C6C' }}>Log in</Link>
          </div>
        </form>
      </div>

      {/* ---- Right: benefits panel ---- */}
      <div className='reg-panel'>
        <div style={{ font: '600 22px/1.35 Lora,serif', color: '#fff', marginBottom: 18 }}>
          {PANEL_TITLE[role]}
        </div>
        {panelPerks.map(perk => (
          <div key={perk} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{
              width: 18, height: 18, borderRadius: '50%', background: 'rgba(15,124,108,0.3)',
              color: '#7ee0cd', display: 'flex', alignItems: 'center', justifyContent: 'center',
              font: '700 10px Inter,sans-serif', flexShrink: 0, marginTop: 2,
            }}>✓</div>
            <div style={{ font: '400 13.5px/1.55 Inter,sans-serif', color: 'rgba(255,255,255,0.75)' }}>{perk}</div>
          </div>
        ))}
        <div style={{ marginTop: 16, padding: 15, background: 'rgba(255,255,255,0.07)', borderRadius: 11, font: '400 13px/1.6 Inter,sans-serif', color: 'rgba(255,255,255,0.7)' }}>
          $9.99 per instructor · first 3 seats free for month one.
        </div>
      </div>
    </div>
  )
}

// SHA-256 hash of the invite token (same algorithm the backend uses when creating invitations)
async function hashToken(token: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(token)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('')
}
